import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import Popup from "reactjs-popup"
import DropdownCombobox from "../DailyCalendarFiles/CustomCalendar/FormElements/DropdownCombobox" // Import your DropdownCombobox component
import { setIsEditing } from "../../features/schedule/scheduleSlice"
import {
	currentlyNurseRealTimeOngoing,
	currentlyNurseLunchBreakOngoing,
	calculateNurseAcuity
} from "../../utils/realTimeNurseUtils"
import useFetchSettings from "../../hooks/useFetchSettings"

const LunchBreakPopup = ({
	isOpen,
	setIsOpen,
	onConfirm,
	onCancel,
	acuity,
	currentNurseId,
	popupTitle = "Start Lunch Break", // Default title
	popupMessage = "Assign your patients to another nurse:", // Default message
	dropdownLabel = "Enter Nurse Assigned..." // Default dropdown label
}) => {
	const patientSchedule = useSelector(state => state.schedule.patientSchedule)
	const nurseSchedule = useSelector(state => state.schedule.nurseSchedule)
	const { settingsData } = useFetchSettings()
	const maxAcuity = settingsData?.maxAcuityForNurse
	const dispatch = useDispatch()

	const sortedNurses = useMemo(() => {
		const availableNurses = nurseSchedule.filter(
			nurse =>
				nurse.nurseId !== currentNurseId &&
				!currentlyNurseLunchBreakOngoing(nurse) &&
				currentlyNurseRealTimeOngoing(nurse, false, false)
		)
		return calculateNurseAcuity(
			availableNurses,
			patientSchedule,
			undefined,
			undefined,
			false,
			false
		).sort((a, b) => a.currentAcuity - b.currentAcuity)
	}, [nurseSchedule, patientSchedule, currentNurseId])

	const calculateAvailability = useCallback(
		() =>
			sortedNurses.map(nurse => {
				const currentAcuity = nurse.currentAcuity || 0
				const isCurrentlyWorking = currentlyNurseRealTimeOngoing(nurse, false, false)
				return isCurrentlyWorking && 15 - currentAcuity >= acuity
			}),
		[sortedNurses, acuity]
	)

	const [nurseAssigned, setNurseAssigned] = useState(
		sortedNurses.length > 0 ? sortedNurses[0].nurseId : "auto"
	)

	const [isSaving, setIsSaving] = useState(false)

	useEffect(() => {
		if (nurseAssigned === "auto" && sortedNurses.length > 0) {
			setNurseAssigned(sortedNurses[0].nurseId)
			dispatch(setIsEditing(true))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sortedNurses])

	const handleClose = () => {
		setIsOpen(false)
		if (!isSaving) {
			dispatch(setIsEditing(false))
		}
		onCancel()
	}

	return (
		<Popup
			modal
			nested
			open={isOpen}
			onClose={handleClose}
			contentStyle={{
				width: "80%",
				maxWidth: "600px",
				border: "none",
				borderRadius: "10px",
				padding: "0"
			}}
			overlayStyle={{ background: "rgba(0, 0, 0, 0.5)" }}
		>
			{close => (
				<div className="bg-white p-6 rounded-xl shadow-md w-full mx-auto">
					<div className="text-xl font-semibold mb-4 text-custom-blue">{popupTitle}</div>
					<p className="text-lg font-medium text-gray-700 mb-4">{popupMessage}</p>

					<DropdownCombobox
						label={dropdownLabel}
						options={sortedNurses}
						value={nurseAssigned}
						onChange={setNurseAssigned}
						displayKey={nurse =>
							`${nurse.nurseName} (Acuity: ${nurse.currentAcuity || 0})`
						} // Show name and acuity
						valueKey="nurseId"
						calculateAvailability={calculateAvailability} // Pass the availability function
						newPatientValue={acuity}
						maxAcuity={maxAcuity} // Example maximum acuity
						defaultValue="auto"
					/>

					<div className="flex justify-end mt-6">
						<button
							onClick={() => {
								onCancel()
								close()
							}}
							className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md mr-2"
						>
							Cancel
						</button>
						<button
							onClick={() => {
								onConfirm(nurseAssigned)
								setIsSaving(true)
								close()
							}}
							className="bg-customBlue text-white px-4 py-2 rounded-md"
							disabled={!nurseAssigned} // Disable confirm if no nurse is selected
						>
							Confirm
						</button>
					</div>
				</div>
			)}
		</Popup>
	)
}

export default LunchBreakPopup
